import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, Check, ArrowUp, ArrowDown } from 'lucide-react';
import {
  TblWrap, TblToolbar, Table, CellPrimary, CellSub, RowIco, CellFlex,
  Actions, Cbx, Bulkbar, Seg, Skeleton,
} from './primitives';

/* styled cell so the empty-state text picks up the theme via styled-components
   (which degrades gracefully without a ThemeProvider) instead of the useTheme
   hook, which throws when used outside a provider. */
const EmptyTd = styled.td`
  text-align: center;
  height: 120px;
  color: ${({ theme }) => theme.textMuted};
  font-size: ${({ theme }) => theme.fsSm};
`;

/* Pagination footer — keeps only `pageSize` rows in the DOM at a time
   so large lists render fast (server-side paging can replace this later). */
const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 14px;
  border-top: 1px solid ${({ theme }) => theme.border};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};

  button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 30px;
    padding: 0 12px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 650;
    cursor: pointer;
  }
  button:hover:not(:disabled) { background: ${({ theme }) => theme.surface2}; }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SortThBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
  user-select: none;

  &:hover { color: ${({ theme }) => theme.text}; }

  .sort-icons {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    margin-left: 2px;
    flex-shrink: 0;
    opacity: ${({ $active }) => ($active ? 1 : 0.35)};
  }

  svg { flex: none; }
`;

/* ============================================================
   DataTable — a 1:1 reusable port of the reference panel's
   `.tbl-wrap` table: toolbar (search + segmented filter),
   optional bulk-select bar, sticky uppercase headers, a colored
   short-name box (`row-ico`) before each row, cell-primary/sub,
   and per-row action buttons. Same fonts / pixels as the
   reference. Pure presentation — it drives the caller's own data
   and handlers, so screen functionality is unchanged.

   Props:
     rows, rowKey(r,i)
     primary { header, label(r), sub?(r), short?(r), color?(r) }
     columns [{ header, render(r)|key, muted, mono, width }]
     actions (r) => JSX                (per-row .actions content)
     search, searchPlaceholder, searchKeys [str|fn]
     filters [{ key, label, test(r) }] (segmented control)
     selectable, onSelectionChange(rows[]), bulkActions (JSX)
     toolbarRight (JSX), onRowClick(r), loading, emptyText
     sortBy, sortDir ('asc'|'desc'), onSortChange(sortKey)
     columns/primary may set sortKey to enable header sorting
   ============================================================ */

const ICO_COLORS = ['#5b54e6', '#2f74e0', '#10977a', '#e0922f', '#8b3ff0', '#d6455d', '#0f9bb0', '#7a8b1f'];
const hashStr = (s) => { let h = 0; const str = String(s || '?'); for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0; return h; };
const icoColor = (label) => ICO_COLORS[hashStr(label) % ICO_COLORS.length];
const toInitials = (name) => String(name || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
const resolve = (accessor, row, i) => (typeof accessor === 'function' ? accessor(row, i) : row[accessor]);

export default function DataTable({
  rows = [], rowKey, primary, columns = [], actions, loading = false,
  search = false, searchPlaceholder = 'Search…', searchKeys,
  filters, selectable = false, onSelectionChange, bulkActions, toolbarRight,
  searchExtra,
  onRowClick, emptyText = 'No records found.', pageSize = 0,
  serverSide = false, searchValue, onSearchChange,
  page: serverPage = 1, totalPages: serverTotalPages = 1, totalElements = 0, onPageChange,
     sortBy, sortDir, onSortChange,
  actionsColumnWidth = 84,
}) {
  const [q, setQ] = useState('');
  const [filterKey, setFilterKey] = useState(filters ? filters[0].key : null);
  const [sel, setSel] = useState(() => new Set());
  const [page, setPage] = useState(1);

  const keyOf = (r, i) => (rowKey ? rowKey(r, i) : i);
  const activeSearchValue = serverSide ? (searchValue ?? '') : q;
  const handleSearchChange = (value) => {
    if (serverSide && onSearchChange) onSearchChange(value);
    else setQ(value);
  };

  const filtered = useMemo(() => {
    let out = Array.isArray(rows) ? rows : [];
    if (filters && filterKey) {
      const f = filters.find((x) => x.key === filterKey);
      if (f && f.test) out = out.filter(f.test);
    }
    if (!serverSide && search) {
      const term = q.trim().toLowerCase();
      if (term) {
        out = out.filter((r) => {
          const keys = searchKeys || Object.keys(r || {});
          return keys.some((k) => {
            const v = typeof k === 'function' ? k(r) : r[k];
            return v != null && String(v).toLowerCase().includes(term);
          });
        });
      }
    }
    return out;
  }, [rows, filters, filterKey, q, search, searchKeys, serverSide]);

  const ids = filtered.map((r, i) => keyOf(r, i));
  const allOn = ids.length > 0 && ids.every((id) => sel.has(id));

  const emit = (next) => {
    setSel(next);
    if (onSelectionChange) onSelectionChange(filtered.filter((r, i) => next.has(keyOf(r, i))));
  };
  const toggleAll = () => emit(allOn ? new Set() : new Set(ids));
  const toggleOne = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); emit(n); };

  // client-side pagination (pageSize=0 disables it); serverSide uses caller-provided page metadata
  const paginate = serverSide ? pageSize > 0 : pageSize > 0;
  const totalPages = serverSide ? Math.max(1, serverTotalPages) : (paginate ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1);
  const safePage = serverSide ? Math.min(Math.max(serverPage, 1), totalPages) : Math.min(page, totalPages);
  const startIndex = paginate && !serverSide ? (safePage - 1) * pageSize : 0;
  const pageRows = serverSide ? filtered : (paginate ? filtered.slice(startIndex, startIndex + pageSize) : filtered);
  const visibleCount = serverSide ? totalElements : filtered.length;
  const rangeStart = serverSide
    ? (visibleCount === 0 ? 0 : ((safePage - 1) * pageSize) + 1)
    : (paginate ? startIndex + 1 : 1);
  const rangeEnd = serverSide
    ? Math.min(safePage * pageSize, visibleCount)
    : (paginate ? Math.min(startIndex + pageSize, filtered.length) : filtered.length);

  // reset to the first page when the filter/search/data size changes (client mode only)
  useEffect(() => {
    if (!serverSide) setPage(1);
  }, [q, filterKey, filtered.length, serverSide]);

  const colCount = (selectable ? 1 : 0) + (primary ? 1 : 0) + columns.length + (actions ? 1 : 0);

  const renderSortHeader = (label, sortKey) => {
    if (!sortKey || !onSortChange) return label;
    const active = sortBy === sortKey;
    const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
    return (
      <SortThBtn
        type="button"
        $active={active}
        aria-sort={ariaSort}
        onClick={() => onSortChange(sortKey)}
      >
        <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
        <span className="sort-icons" aria-hidden="true">
          {active && sortDir === 'asc' ? (
            <ArrowUp size={11} strokeWidth={2.5} />
          ) : active && sortDir === 'desc' ? (
            <ArrowDown size={11} strokeWidth={2.5} />
          ) : (
            <>
              <ArrowUp size={9} strokeWidth={2} />
              <ArrowDown size={9} strokeWidth={2} />
            </>
          )}
        </span>
      </SortThBtn>
    );
  };

  return (
    <TblWrap>
      {(search || filters || toolbarRight || searchExtra) && (
        <TblToolbar>
          {search && (
            <div className="search">
              <Search size={16} />
              <input
                placeholder={searchPlaceholder}
                value={activeSearchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          )}
          {searchExtra}
          {filters && (
            <Seg>
              {filters.map((f) => (
                <button key={f.key} className={filterKey === f.key ? 'is-on' : ''} onClick={() => setFilterKey(f.key)}>{f.label}</button>
              ))}
            </Seg>
          )}
          {toolbarRight && <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>{toolbarRight}</div>}
        </TblToolbar>
      )}

      {selectable && sel.size > 0 && (
        <Bulkbar>
          <span><b className="tnum">{sel.size}</b> selected</span>
          <div style={{ flex: 1 }} />
          {bulkActions}
        </Bulkbar>
      )}

      <div style={{ overflowX: 'auto' }}>
        <Table>
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: 44 }}>
                  <Cbx as="span" $on={allOn} onClick={toggleAll} title="Select all"><Check size={13} /></Cbx>
                </th>
              )}
              {primary && (
                <th style={{
                  ...(primary.width ? { width: primary.width } : {}),
                  ...(primary.minWidth ? { minWidth: primary.minWidth } : {}),
                }}>
                  {renderSortHeader(primary.header || 'Name', primary.sortKey)}
                </th>
              )}
              {columns.map((c, i) => (
                <th
                  key={i}
                  style={{
                    ...(c.width ? { width: c.width } : {}),
                    ...(c.minWidth ? { minWidth: c.minWidth } : {}),
                  }}
                >
                  {renderSortHeader(c.header, c.sortKey)}
                </th>
              ))}
              {actions && <th style={{ width: actionsColumnWidth }} aria-label="actions" />}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: paginate ? Math.min(pageSize, 8) : 6 }).map((_, ri) => (
              <tr key={`sk-${ri}`} aria-hidden="true">
                {selectable && (
                  <td style={{ width: 44 }}><Skeleton $w="16px" $h="16px" $r="4px" /></td>
                )}
                {primary && (
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                      <Skeleton $circle $h="34px" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Skeleton $w="58%" $h="12px" $mb="6px" />
                        <Skeleton $w="38%" $h="10px" />
                      </div>
                    </div>
                  </td>
                )}
                {columns.map((c, ci) => (
                  <td key={ci}><Skeleton $w={['70%', '55%', '80%', '45%'][ci % 4]} $h="12px" /></td>
                ))}
                {actions && (
                  <td><Skeleton $w="20px" $h="20px" $r="6px" /></td>
                )}
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><EmptyTd colSpan={colCount}>{emptyText}</EmptyTd></tr>
            )}
            {!loading && pageRows.map((r, i) => {
              const id = keyOf(r, startIndex + i);
              const on = sel.has(id);
              let label, sub, short, color;
              if (primary) {
                label = resolve(primary.label, r, i);
                sub = primary.sub != null ? resolve(primary.sub, r, i) : null;
                short = primary.short ? primary.short(r, i) : toInitials(label);
                color = primary.color ? primary.color(r, i) : icoColor(label);
              }
              return (
                <tr
                  key={id}
                  className={on ? 'is-sel' : ''}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <Cbx $on={on} onClick={() => toggleOne(id)}><Check size={13} /></Cbx>
                    </td>
                  )}
                  {primary && (
                    <td>
                      <CellFlex>
                        <RowIco style={{ background: `${color}22`, color }}>{short}</RowIco>
                        <div>
                          <CellPrimary>{label}</CellPrimary>
                          {sub != null && sub !== '' && <CellSub>{sub}</CellSub>}
                        </div>
                      </CellFlex>
                    </td>
                  )}
                  {columns.map((c, ci) => (
                    <td key={ci} className={[c.muted ? 'muted' : '', c.mono ? 'tnum' : '', c.wrap ? 'wrap' : ''].filter(Boolean).join(' ') || undefined}>
                      {c.render ? c.render(r, i) : r[c.key]}
                    </td>
                  ))}
                  {actions && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <Actions style={{ opacity: 1 }}>{actions(r)}</Actions>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {paginate && (serverSide ? (visibleCount > 0 || safePage > 1) : filtered.length > 0) && (
        <Pager aria-busy={loading || undefined}>
          <span>
            {loading ? (
              <>Loading…</>
            ) : (
              <>{rangeStart}–{rangeEnd} of {visibleCount}</>
            )}
          </span>
          <button
            type="button"
            onClick={() => (serverSide && onPageChange ? onPageChange(safePage - 1) : setPage((p) => Math.max(1, p - 1)))}
            disabled={safePage <= 1}
          >
            Prev
          </button>
          <span className="tnum">Page {safePage} / {totalPages}</span>
          <button
            type="button"
            onClick={() => (serverSide && onPageChange ? onPageChange(safePage + 1) : setPage((p) => Math.min(totalPages, p + 1)))}
            disabled={safePage >= totalPages}
          >
            Next
          </button>
        </Pager>
      )}
    </TblWrap>
  );
}
